import React from 'react';
import Measure from 'react-measure';
import Switch from '@material-ui/core/Switch';
import PropTypes from 'prop-types';

import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell } from 'recharts';
import { Button } from '@material-ui/core';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];
const RADIAN = Math.PI / 180;

// eslint-disable-next-line react/prop-types
const MyPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, payload }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const query = { method: payload.name };
    if (payload.type === 'exception') {
        query.error = { $ne: null };
    } else if (payload.type === 'error') {
        query['response.errorCode'] = { $gt: 0 };
    }
    const state = { rpc_query: JSON.stringify(query, null, 4), rpc_limit: payload.value };
    const url = `/logs#${encodeURIComponent(JSON.stringify(state))}`;
    return (
        <a href={url} target="_blank" rel="noopener noreferrer">
            <text x={x} y={y} fill="white">
                {payload.name}: {payload.value}
            </text>
        </a>
    );
};

class DataFetcher extends React.PureComponent {
    state = {
        loading: true,
        data: null,
        date: null,
    };

    displayLength = 24 * 60 * 60 * 1000;
    updateInterval = 60 * 1000;
    dataInterval = 5 * 60 * 1000;

    constructor(props) {
        super(props);
        // Initial sync.
        this.state.date = props.date;
        this.date = props.date;
        this.updateCount = 0;
    }

    getLastTime() {
        return this.props.date == null ? new Date() : new Date(this.props.date);
    }

    autoUpdate() {
        return !this.props.date;
    }

    normalize = record => {
        record.localTime = new Date(record.time).toLocaleString();
        return record;
    }

    static getDerivedStateFromProps(props, state) {
        if (props.date !== state.date) {
            return { date: props.date, loading: true };
        }
        return null;
    }

    componentDidMount() {
        this.fetchInitial();
    }

    componentWillUnmount() {
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = null;
        }
        this.updateCount++;
    }

    fetchInitial() {
        const updateCount = this.updateCount;
        const to = this.getLastTime();
        const from = new Date(to.getTime() - this.displayLength);
        fetch(`/api/data/analytics/summary?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`)
            .then(response => response.json())
            .then(result => {
                if (updateCount === this.updateCount) {
                    this.setState({
                        loading: false, data: result.results.map(this.normalize)
                    });
                }
            });
        if (this.autoUpdate()) {
            this.timer = setInterval(this.fetchUpdate, this.updateInterval);
        }
    }

    fetchUpdate = () => {
        // Wait for the first fetch to finish.
        if (this.state.loading) {
            return;
        }
        const origin = this.timer;
        const to = new Date();
        const from = new Date(to.getTime() - 2 * this.updateInterval - 2 * 10 * 1000);
        fetch(`/api/data/analytics/summary?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`)
            .then(response => response.json())
            .then(result => {
                if (origin === this.timer) {
                    this.updateData(result.results.map(this.normalize));
                }
            });
    }

    updateData(results) {
        if (results.length === 0) return;
        if (this.state.data.length === 0) {
            this.setState({ data: results });
            return;
        }
        const data = this.state.data.slice();
        for (let item of results) {
            if (item.time > data[data.length - 1].time) {
                data.push(item);
            }
        }
        if (data.length > this.displayLength / this.dataInterval) {
            this.setState({ data: data.slice(data.length - this.displayLength / this.dataInterval) });
            return;
        }
        this.setState({ data });
    }

    refresh() {
        console.assert(this.state.loading);
        this.updateCount++;
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = null;
        }
        this.fetchInitial();
    }

    render() {
        if (this.props.date !== this.date) {
            this.date = this.props.date;
            setTimeout(() => this.refresh());
        }
        return this.props.children(this.state.data || []);
    }
}

DataFetcher.propTypes = {
    date: PropTypes.number,
    children: PropTypes.func.isRequired,
};

class Graph extends React.PureComponent {
    state = {
        bounds: null,
        showSuccess: true,
        showError: true,
        showException: true,
        daysAgo: '',
    };

    onResize = rect => {
        this.setState({ bounds: rect.bounds });
    }

    handleSwitch = name => () => {
        this.setState({ [name]: !this.state[name] });
    }

    handleInput = name => e => {
        this.setState({ [name]: e.target.value });
    }

    applyDaysAgo = () => {
        this.props.onChangeDaysAgo(Number(this.state.daysAgo));
    }

    render() {
        const successRequests = {};
        for (let item of this.props.data) {
            for (let key in item.summary.successRequests) {
                const value = item.summary.successRequests[key];
                if (successRequests[key] === undefined) {
                    successRequests[key] = value;
                } else {
                    successRequests[key] += value;
                }
            }
        }
        const pie0Data = Object.keys(successRequests)
            .sort((a, b) => successRequests[b] - successRequests[a])
            .map(key => ({ name: key, value: successRequests[key], type: 'success' }));

        const errorRequests = {};
        for (let item of this.props.data) {
            for (let key in item.summary.errorRequests) {
                const value = item.summary.errorRequests[key];
                if (errorRequests[key] === undefined) {
                    errorRequests[key] = value;
                } else {
                    errorRequests[key] += value;
                }
            }
        }
        const pie1Data = Object.keys(errorRequests)
            .sort((a, b) => errorRequests[b] - errorRequests[a])
            .map(key => ({ name: key, value: errorRequests[key], type: 'error' }));

        const exceptionRequests = {};
        for (let item of this.props.data) {
            for (let key in item.summary.exceptionRequests) {
                const value = item.summary.exceptionRequests[key];
                if (exceptionRequests[key] === undefined) {
                    exceptionRequests[key] = value;
                } else {
                    exceptionRequests[key] += value;
                }
            }
        }
        const pie2Data = Object.keys(exceptionRequests)
            .sort((a, b) => exceptionRequests[b] - exceptionRequests[a])
            .map(key => ({ name: key, value: exceptionRequests[key], type: 'exception' }));

        return (
            <div>
                <h4>Requests Last 24 Hours</h4>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                        <Switch checked={this.state.showSuccess} onChange={this.handleSwitch('showSuccess')} color="primary" /><span>Show Success</span>
                        <Switch checked={this.state.showError} onChange={this.handleSwitch('showError')} color="primary" /><span>Show Error</span>
                        <Switch checked={this.state.showException} onChange={this.handleSwitch('showException')} color="primary" /><span>Show Exception</span>
                    </div>
                    <div>
                        <input value={this.state.daysAgo} onChange={this.handleInput('daysAgo')} /><span>days ago</span>
                        <Button onClick={this.applyDaysAgo}>Apply</Button>
                    </div>
                </div>
                <Measure bounds onResize={this.onResize}>
                    {
                        ({ measureRef }) => (
                            <div style={{ paddingRight: '16px' }}>
                                <div ref={measureRef}>
                                    {
                                        this.state.bounds &&
                                        <LineChart width={Math.max(this.state.bounds.width, 800)} height={600} data={this.props.data}>
                                            <CartesianGrid stroke="#ccc" />
                                            <XAxis dataKey="localTime" />
                                            <YAxis />
                                            {
                                                this.state.showSuccess &&
                                                <Line isAnimationActive={false} type="monotone" name="success" dataKey="summary.successCount" stroke="green" />
                                            }
                                            {
                                                this.state.showError &&
                                                <Line isAnimationActive={false} type="monotone" name="error" dataKey="summary.errorCount" stroke="red" />
                                            }
                                            {
                                                this.state.showException &&
                                                <Line isAnimationActive={false} type="monotone" name="exception" dataKey="summary.exceptionCount" stroke="black" />
                                            }
                                            <Tooltip />
                                            <Legend />
                                        </LineChart>
                                    }
                                </div>
                            </div>
                        )
                    }
                </Measure>

                <h4>Success Requests</h4>
                <PieChart width={600} height={600}>
                    <Pie isAnimationActive={false} data={pie0Data} dataKey="value" labelLine={false} label={MyPieLabel}>
                        {
                            pie0Data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)
                        }
                    </Pie>
                </PieChart>

                <h4>Error Requests</h4>
                <PieChart width={600} height={600}>
                    <Pie isAnimationActive={false} data={pie1Data} dataKey="value" labelLine={false} label={MyPieLabel}>
                        {
                            pie1Data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)
                        }
                    </Pie>
                </PieChart>

                <h4>Exception Requests</h4>
                <PieChart width={600} height={600}>
                    <Pie isAnimationActive={false} data={pie2Data} dataKey="value" labelLine={false} label={MyPieLabel}>
                        {
                            pie2Data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)
                        }
                    </Pie>
                </PieChart>
            </div>
        );
    }
}

Graph.propTypes = {
    data: PropTypes.array,
    onChangeDaysAgo: PropTypes.func,
};

export class Dashboard extends React.PureComponent {
    state = {
        date: null,
    };

    constructor(props) {
        super(props);

        if (window.location.hash) {
            const date = new Date(window.location.hash.substring(1));
            if (!isNaN(date.getTime())) {
                this.state.date = date.getTime();
            }
        }
    }

    applyDaysAgo = days => {
        if (days) {
            const date = Date.now() - days * 24 * 3600 * 1000;
            window.location.hash = new Date(date).toISOString();
            this.setState({ date });
        } else {
            window.location.hash = '';
            this.setState({ date: null });
        }
    }

    render() {
        return (
            <DataFetcher date={this.state.date}>
                {
                    (data) => (
                        <Graph data={data} onChangeDaysAgo={this.applyDaysAgo} />
                    )
                }
            </DataFetcher>
        );
    }
}
